import yaml from "js-yaml"
import { ObjectType } from "@/models/object_type"

// Comprehensive data type mapping (covers common databases and integrations)
export const DATATYPE_MAPPING = {
  Json: ["json", "jsonb", "reference", "dict", "object", "map"],
  String: [
    "charfield",
    "textfield",
    "emailfield",
    "urlfield",
    "autofield",
    "string",
    "uuid",
    "str",
    "textarea",
    "text",
    "url",
    "email",
    "phone",
    "image",
    "fax",
    "combobox",
    "picklist",
    "anytype",
    "enum",
    "calculated",
    "select",
    "id",
    "character varying",
    "character",
    "varchar",
    "nvarchar",
    "nchar",
    "citext",
    "clob",
    "mediumtext",
    "longtext",
    "bpchar"
  ],
  Number: [
    "hugeint",
    "decimalfield",
    "integerfield",
    "smallintegerfield",
    "decimal",
    "number",
    "integer",
    "float",
    "int",
    "tinyint",
    "double",
    "percent",
    "currency",
    "barrating",
    "starrating",
    "bigint",
    "smallint",
    "numeric",
    "real",
    "double precision",
    "mediumint",
    "serial",
    "bigserial",
    "money",
    "int2",
    "int4",
    "int8"
  ],
  Boolean: ["booleanfield", "boolean", "bool", "checkbox", "togglebutton", "bit"],
  Time: ["timefield", "time", "time with time zone", "timetz"],
  DateTime: [
    "datetimefield",
    "date-time",
    "timestamp",
    "datetime",
    "timestamp without time zone",
    "timestamp with time zone",
    "timestamptz",
    "smalldatetime",
    "datetime2"
  ],
  Date: ["datefield", "date"],
  List: [
    "multipicklist",
    "list",
    "checklist",
    "junctionidlist",
    "orderedlist",
    "bulletpointslist",
    "enumeration",
    "array",
    "set",
    "varchar[]",
    "text[]",
    "json[]"
  ],
  TimeDelta: ["timedelta", "timedeltafield", "interval"],
  Binary: ["blob", "binary", "bytea", "varbinary", "longblob", "mediumblob"],
  File: ["file", "attachment", "document"]
}

// Comprehensive input type mapping
export const INPUTTYPE_MAPPING = {
  ObjectReference: ["reference", "json", "dict", "object"],
  Text: [
    "varchar",
    "nvarchar",
    "charfield",
    "textfield",
    "autofield",
    "string",
    "uuid",
    "str",
    "text",
    "anytype",
    "calculated",
    "enum",
    "id",
    "character varying",
    "character",
    "mediumtext",
    "longtext",
    "bpchar"
  ],
  Image: ["image"],
  File: ["file", "blob", "attachment", "document"],
  Email: ["emailfield", "email"],
  URL: ["urlfield", "url"],
  Textarea: ["textarea", "clob"],
  Select: ["combobox", "picklist", "select"],
  Phone: ["phone", "fax"],
  Number: [
    "integerfield",
    "smallintegerfield",
    "number",
    "tinyint",
    "hugeint",
    "bigint",
    "int",
    "barrating",
    "starrating",
    "mediumint",
    "serial",
    "bigserial"
  ],
  Currency: ["currency", "money"],
  Percent: ["percent"],
  Decimal: ["decimalfield", "decimal", "float", "double", "numeric", "real", "double precision"],
  Checkbox: ["booleanfield", "boolean", "bool", "checkbox", "togglebutton", "bit"],
  Time: ["timefield", "time", "timetz"],
  DateTime: [
    "datetimefield",
    "date-time",
    "datetime",
    "timestamp without time zone",
    "timestamp with time zone",
    "timestamptz",
    "timestamp",
    "smalldatetime",
    "datetime2"
  ],
  Date: ["datefield", "date"],
  Checklist: ["multipicklist", "list", "checklist", "junctionidlist", "enumeration", "set", "array", "varchar[]", "text[]", "json[]"],
  TimeDelta: ["timedelta", "timedeltafield", "interval"],
  Binary: ["binary", "varbinary", "bytea", "blob"]
}

// Inverse lookups for efficient mapping
export const DATATYPE_LOOKUP = {}
// Build the inverse lookup for data types
Object.entries(DATATYPE_MAPPING).forEach(([dataType, sourceTypes]) => {
  sourceTypes.forEach((sourceType) => {
    DATATYPE_LOOKUP[sourceType.toLowerCase()] = dataType
  })
})

export const INPUTTYPE_LOOKUP = {}
// Build the inverse lookup for input types
Object.entries(INPUTTYPE_MAPPING).forEach(([inputType, sourceTypes]) => {
  sourceTypes.forEach((sourceType) => {
    INPUTTYPE_LOOKUP[sourceType.toLowerCase()] = inputType
  })
})

/**
 * Returns RevSetter primitive data type from various source data type names.
 * @param {string} value - Source data type name
 * @returns {string} - Standardized RevSetter data type or 'NOT_SUPPORTED'
 */
export const getLocalSourceDatatype = (value) => {
  if (!value) {
    return "NOT_SUPPORTED"
  }

  return DATATYPE_LOOKUP[value.trim().toLowerCase()] || "NOT_SUPPORTED"
}

/**
 * Returns RevSetter primitive input type from source data type names.
 * @param {string} value - Source data type name
 * @returns {string} - Standardized RevSetter input type or 'NOT_SUPPORTED'
 */
export const getLocalSourceInputtype = (value) => {
  if (!value) {
    return "NOT_SUPPORTED"
  }

  const valueClean = value.trim().toLowerCase()

  // Direct lookup first
  const inputType = INPUTTYPE_LOOKUP[valueClean]
  if (inputType) {
    return inputType
  }

  // Fallback to datatype-based inference
  const datatype = getLocalSourceDatatype(valueClean)
  const fallbackMapping = {
    String: "Text",
    Number: "Number",
    Boolean: "Checkbox",
    DateTime: "DateTime",
    Date: "Date",
    Time: "Time",
    Json: "ObjectReference",
    List: "Checklist",
    Binary: "File",
    File: "File",
    TimeDelta: "TimeDelta"
  }

  return fallbackMapping[datatype] || "NOT_SUPPORTED"
}

export const objects_schema = {
  task: {
    $schema: "http://json-schema.org/draft-07/schema#",
    type: "object",
    properties: {
      status: { type: "string", default: "pending", enum: ["pending", "in-progress", "completed", "cancelled"] },
      auto_start_date_set: { type: "boolean" },
      in_progress_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      start_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      due_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      due_date_relative_to: {
        oneOf: [{ type: "string", enum: ["datetime_created", "start_date", "in_progress_date"] }, { type: "null" }]
      },
      due_date_days_offset: { type: "integer", maximum: 2147483647, minimum: 0 },
      name: { type: "string", required: true, maxLength: 150 },
      description: { oneOf: [{ type: "string" }, { type: "null" }] },
      priority: { type: "string", enum: ["lowest", "low", "normal", "high", "highest"] },
      record: { oneOf: [{ type: "string", description: "UUID for Record", object: "Record" }, { type: "null" }] },
      contacts: { type: "array", items: { type: "string", object: "Contact" } },
      assigned_to: { type: "array", items: { type: "string", format: "uuid", object: "User" } },
      action_tags: { type: "array", items: { type: "string" } },
      attachments: { type: "array", items: { type: "string", object: "Attachment", format: "uuid" } }
    }
  },
  goal: {
    $schema: "http://json-schema.org/draft-07/schema#",
    type: "object",
    properties: {
      filter: { type: "string" },
      auto_start_date_set: { type: "boolean" },
      in_progress_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      start_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      due_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      due_date_relative_to: {
        oneOf: [{ type: "string", enum: ["datetime_created", "start_date", "in_progress_date"] }, { type: "null" }]
      },
      due_date_days_offset: { type: "integer", maximum: 2147483647, minimum: 0 },
      name: { type: "string", maxLength: 300 },
      type: { type: "string", enum: ["specific", "measurable", "attainable", "relevant", "time-Bound"] },
      status: { type: "string", enum: ["pending", "in-progress", "completed", "missed"] },
      description: { oneOf: [{ type: "string" }, { type: "null" }] },
      record: { oneOf: [{ type: "string", description: "UUID for Record", object: "Record" }, { type: "null" }] },
      companies: { type: "array", items: { type: "string", object: "Record" } },
      records: { type: "array", items: { type: "string", object: "Record" } },
      assigned_to: { type: "array", items: { type: "string", object: "User" } },
      contacts: { type: "array", items: { type: "string", object: "Contact" } },
      action_tags: { type: "array", items: { type: "string" } },
      tasks: { type: "array", items: { type: "string" } },
      notes: { type: "array", items: { type: "string" } },
      attachments: { type: "array", items: { type: "string" } }
    },
    required: ["name"]
  },
  interaction: {
    $schema: "http://json-schema.org/draft-07/schema#",
    type: "object",
    properties: {
      type: { type: "string", enum: ["note", "meeting", "in-person-meeting", "call", "email", "support", "marketing", "other"] },
      category: {
        type: "string",
        enum: [
          "renewal",
          "support",
          "expansion",
          "onboarding",
          "business_review",
          "business",
          "review",
          "consultation",
          "training",
          "ticket",
          "chat",
          "in-app",
          "webinar",
          "campaign",
          "inbound",
          "resources",
          "website",
          "qbr",
          "ebr",
          "sales",
          "cross-sale",
          "demo",
          "discovery",
          "cold_call",
          "pitch",
          "follow_up",
          "other"
        ]
      },
      record: { oneOf: [{ type: "string", description: "UUID for Record", object: "Record" }, { type: "null" }] },
      companies: { type: "array", items: { type: "string", object: "Record" } },
      records: { type: "array", items: { type: "string", object: "Record" } },
      name: { type: "string" },
      template_id: { oneOf: [{ type: "string" }, { type: "null" }] },
      assign_companies_to_goals_and_tasks: { type: "boolean" },
      assign_collaborators_to_goals_and_tasks: { type: "boolean" },
      description: { oneOf: [{ type: "string" }, { type: "null" }] },
      interaction_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      origin: {
        type: "string",
        enum: ["salesforce", "hubspot", "postgres", "revsetter", "spreadsheet", "platform-maps", "gmail", "outlook"]
      },
      parent: { type: "string", description: "UUID for Interaction", object: "Interaction" },
      action_tags: { type: "array", items: { type: "string" } },
      users: { type: "array", items: { type: "string", object: "User" } },
      contacts: { type: "array", items: { type: "string", object: "Contact" } },
      attachments: { type: "array", items: { type: "string" } }
    },
    required: ["type"]
  },
  milestone: {
    $schema: "http://json-schema.org/draft-07/schema#",
    type: "object",
    properties: {
      steps: { type: "array", items: { type: "string" } },
      collaborators: { type: "array", items: { type: "string" } },
      companies: { type: "array", items: { type: "string", object: "Company" } },
      contacts: { type: "array", items: { type: "string", object: "Contact" } },
      teams: { type: "array", items: { type: "string" } },
      auto_start_date_set: { type: "boolean" },
      in_progress_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      start_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      due_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      due_date_relative_to: {
        oneOf: [{ type: "string", enum: ["datetime_created", "start_date", "in_progress_date"] }, { type: "null" }]
      },
      due_date_days_offset: { type: "integer", maximum: 2147483647, minimum: 0 },
      name: { type: "string", maxLength: 150 },
      description: { oneOf: [{ type: "string" }, { type: "null" }] },
      status: { type: "string", enum: ["pending", "in-progress", "completed", "missed"] },
      show_dynamic_status: { type: "boolean" },
      action_tags: { type: "array", items: { type: "string" } },
      notes: { type: "array", items: { type: "string" } }
    },
    required: ["steps", "name", "status"]
  },
  plan: {
    $schema: "http://json-schema.org/draft-07/schema#",
    type: "object",
    properties: {
      notes: { type: "array", items: { type: "string" } },
      attachments: { type: "array", items: { type: "string" } },
      auto_start_date_set: { type: "boolean" },
      in_progress_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      start_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      due_date: { oneOf: [{ type: "string", format: "date-time" }, { type: "null" }] },
      due_date_relative_to: {
        oneOf: [{ type: "string", enum: ["datetime_created", "start_date", "in_progress_date"] }, { type: "null" }]
      },
      due_date_days_offset: { type: "integer", maximum: 2147483647, minimum: 0 },
      name: { type: "string", maxLength: 150 },
      template_id: { oneOf: [{ type: "string" }, { type: "null" }] },
      assign_companies_to_goals_and_tasks: { type: "boolean" },
      assign_collaborators_to_goals_and_tasks: { type: "boolean" },
      description: { oneOf: [{ type: "string" }, { type: "null" }] },
      type: {
        type: "string",
        enum: ["account_plan", "growth_plan", "success_plan", "onboarding_plan", "health_plan", "renewal_plan", "other"]
      },
      status: { type: "string", enum: ["pending", "in-progress", "completed", "missed"] },
      show_dynamic_status: { type: "boolean" },
      teams: { type: "array", items: { type: "string" } },
      companies: { type: "array", items: { type: "string", object: "Company" } },
      contacts: { type: "array", items: { type: "string", object: "Contact" } },
      collaborators: { type: "array", items: { type: "string", object: "User" } },
      action_tags: { type: "array", items: { type: "string" } },
      goals: {
        type: "array",
        items: { oneOf: [{ type: "string" }, { type: "object" }] }
      },
      tasks: {
        type: "array",
        items: { oneOf: [{ type: "string" }, { type: "object" }] }
      }
    },
    required: ["name", "type"]
  },
  record: {
    $schema: "http://json-schema.org/draft-07/schema#",
    type: "object",
    properties: {
      name: { type: "string", maxLength: 1000 },
      values: { type: "object" },
      origin: { type: "string", default: "revsetter", maxLength: 100 },
      origins: { type: "object" },
      type: { type: "string", maxLength: 100 },
      is_active: { type: "boolean" },
      object_id: { type: "string", maxLength: 400 },
      parent: { type: "string", description: "UUID for Record", object: "Record" },
      object_type: { type: "string", description: "UUID for ObjectType", object: "ObjectType" },
      owner: { type: "string", description: "UUID for User", object: "User" },
      notes: { type: "array", items: { type: "string" } },
      co_owners: { type: "array", items: { type: "string", object: "User" } }
    },
    required: ["values", "origin", "object_id", "object_type"]
  }
}

export const actions_schema = {
  send_email: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        sender: {
          required: true,
          oneOf: [
            { type: "string", format: "email" },
            { type: "string", object: "User" }
          ]
        },
        to: {
          required: true,
          type: "array",
          items: {
            oneOf: [
              { type: "string", format: "email" },
              {
                type: "object",
                properties: {
                  email: { type: "string", format: "email" },
                  first_name: { type: "string" },
                  last_name: { type: "string" }
                },
                required: ["email"]
              }
            ]
          }
        },
        cc: { type: "array", items: { oneOf: [{ type: "string", format: "email" }, { type: "string" }] } },
        bcc: { type: "array", items: { oneOf: [{ type: "string", format: "email" }, { type: "string" }] } },
        groups: { type: "array", items: { type: "string", object: "Group" } },
        cc_groups: { type: "array", items: { type: "string", object: "Group" } },
        bcc_groups: { type: "array", items: { type: "string", object: "Group" } },
        companies: { type: "array", items: { type: "string", object: "Record" } },
        cc_companies: { type: "array", items: { type: "string", object: "Record" } },
        bcc_companies: { type: "array", items: { type: "string", object: "Record" } },
        excluded_contacts: { type: "array", items: { type: "string", object: "Record" } },
        excluded_cc_contacts: { type: "array", items: { type: "string", object: "Record" } },
        excluded_bcc_contacts: { type: "array", items: { type: "string", object: "Record" } },
        subject: { type: "string", required: true, data_type: "string", input_type: "Text", description: "Subject" },
        body: { type: "string", required: true, data_type: "string", input_type: "Text", description: "Body" },
        attachments: { type: "array", items: { type: "string" }, data_type: "string", input_type: "Text", description: "Attachments" }
      }
    },
    output: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        status: { type: "string", enum: ["success", "error"] },
        message_ids: { type: "array", items: { type: "string" } },
        errors: { type: "array", items: { type: "string" } }
      }
    }
  },
  send_slack: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        sender: { type: "string", required: true, data_type: "string", input_type: "Text", description: "Sender" },
        to: { type: "array", items: { type: "string" }, required: true, data_type: "string", input_type: "Text", description: "To" },
        body: { type: "string", required: true, data_type: "string", input_type: "Text", description: "Body" },
        attachments: { type: "array", items: { type: "string" } }
      }
    },
    output: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        status: { type: "string", enum: ["success", "error"] }
      }
    }
  },
  update_task: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        task: { type: "string", required: true, data_type: "string", input_type: "TaskSelect", description: "Task to update" },
        field: { type: "string", required: true, data_type: "string", input_type: "Text", description: "Field to update" },
        value: { type: ["string", "number", "boolean", "array", "object", "null"] }
      }
    },
    output: objects_schema["task"]
  },
  update_goal: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        goal: { type: "string", required: true, data_type: "string", input_type: "GoalSelect", description: "Goal to update" },
        field: {
          type: "string",
          enum: ["status", "name", "description"],
          required: true,
          data_type: "string",
          input_type: "Text",
          description: "Field to update"
        },
        value: { type: ["string", "number", "boolean", "array", "object", "null"] }
      }
    },
    output: objects_schema["goal"]
  },
  update_interaction: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        interaction: {
          type: "string",
          required: true,
          data_type: "string",
          input_type: "InteractionSelect",
          description: "Interaction to update"
        },
        field: { type: "string", required: true, data_type: "string", input_type: "Text", description: "Field to update" },
        value: { type: ["string", "number", "boolean", "array", "object", "null"] }
      }
    },
    output: objects_schema["interaction"]
  },
  update_milestone: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        milestone: {
          type: "string",
          required: true,
          data_type: "string",
          input_type: "MilestoneSelect",
          description: "Milestone to update"
        },
        field: { type: "string", required: true, data_type: "string", input_type: "Text", description: "Field to update" },
        value: { type: ["string", "number", "boolean", "array", "object", "null"] }
      }
    },
    output: objects_schema["milestone"]
  },
  update_plan: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        plan: { type: "string", required: true, data_type: "string", input_type: "PlanSelect", description: "Plan to update" },
        field: { type: "string", required: true, data_type: "string", input_type: "Text", description: "Field to update" },
        value: { type: ["string", "number", "boolean", "array", "object", "null"] }
      }
    },
    output: objects_schema["plan"]
  },
  delete_object: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        id: { type: "string", required: true, data_type: "string", input_type: "Text", description: "ID of the object to delete" }
      }
    },
    output: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        status: { type: "string", enum: ["success", "error"] }
      }
    }
  },
  filter_records: {
    $schema: "http://json-schema.org/draft-07/schema#",
    type: "object",
    properties: {
      filter: { oneOf: [{ type: "string" }, { type: "object" }] },
      object_type: { oneOf: [{ type: "string" }, { type: "null" }] }
    }
  },
  filter_tasks: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        filter: { type: "string", required: true, data_type: "string", input_type: "TaskFilter", description: "Filter to apply" }
      }
    },
    output: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "array",
      items: objects_schema["task"]
    }
  },
  filter_goals: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        filter: { type: "string", required: true, data_type: "string", input_type: "GoalFilter", description: "Filter to apply" }
      }
    },
    output: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "array",
      items: objects_schema["goal"]
    }
  },
  filter_interactions: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        filter: { type: "string", required: true, data_type: "string", input_type: "TaskFilter", description: "Filter to apply" }
      }
    },
    output: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "array",
      items: objects_schema["interaction"]
    }
  },
  filter_plans: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        filter: { type: "string", required: true, data_type: "string", input_type: "PlanFilter", description: "Filter to apply" }
      }
    },
    output: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "array",
      items: objects_schema["plan"]
    }
  },
  filter_milestones: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        filter: { type: "string", required: true, data_type: "string", input_type: "MilestoneFilter", description: "Filter to apply" }
      }
    },
    output: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "array",
      items: objects_schema["milestone"]
    }
  },
  sleep: {
    input: {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {
        duration: {
          type: "string",
          regex: "^P(\\d+D)?(T(\\d+H)?(\\d+M)?(\\d+S)?)?",
          data_type: "string",
          required: true,
          input_type: "number",
          description: "Duration in seconds"
        }
      }
    },
    output: null
  }
}
// get action schema
export function getActionInputSchema(action_type) {
  return actions_schema[action_type]?.input || {}
}

export function getActionOutputSchema(action_type) {
  return actions_schema[action_type]?.output || {}
}

export function getObjectSchema(object_type) {
  /* Returns Record Schema or Activities Schema */
  if (!object_type) {
    return {}
  }
  if (["task", "plan", "milestone", "interaction", "goal"].includes(object_type)) {
    return objects_schema[object_type]
  }
  /* Same as the schema only we add the metrics */
  let object_metrics = ObjectType.of(object_type).metrics || []
  // each metrics have a datatype we use it to generate the schema along with the reference_name
  let schema = {}
  object_metrics.forEach((m) => {
    schema[m.reference_name] = {
      type: m.data_type.toLowerCase(),
      label: m.name
    }
  })
  objects_schema["record"]["properties"]["values"]["properties"] = schema
  return objects_schema["record"]
}

export const getStepName = (step) => {
  // Check if step has trigger
  if (step.trigger) {
    const triggerType = step.trigger.type
    const objectType = step.trigger.object_type
    return `${objectType.charAt(0).toUpperCase() + objectType.slice(1)} ${triggerType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")}`
  }

  // Check if step has parallel branches
  if (step.parallel) {
    return "Parallel execution"
  }

  // Check if step has send_email action
  if (step.args && step.call === "send_email") {
    // Get recipient name from email body
    const bodyMatch = step.args.body.match(/Hi ([^,]+),/)
    const recipientName = bodyMatch ? bodyMatch[1] : "Recipient"
    return `Send Follow-up Email to ${recipientName}`
  }

  // Default case
  return `Step ${step.id}`
}

export const enrichFlowChartWithNames = (flowChart) => {
  // Convert the flow chart to JSON format if it's a string
  const jsonFlowChart = typeof flowChart === "string" ? yaml.load(flowChart) : flowChart

  // Process steps recursively
  const processSteps = (steps) => {
    const enrichedSteps = {}

    steps.forEach((step) => {
      const [stepId, stepData] = Object.entries(step)[0]

      // Handle parallel branches
      if (stepData.parallel) {
        const enrichedBranches = stepData.parallel.branches.map((branch) => {
          const [branchId, branchData] = Object.entries(branch)[0]
          return {
            [branchId]: {
              ...branchData,
              steps: processSteps(branchData.steps)
            }
          }
        })

        enrichedSteps[stepId] = {
          ...stepData,
          name: getStepName(stepData),
          parallel: {
            ...stepData.parallel,
            branches: enrichedBranches
          }
        }
      } else {
        // Handle regular steps
        enrichedSteps[stepId] = {
          ...stepData,
          name: getStepName(stepData)
        }
      }
    })

    return enrichedSteps
  }

  return {
    main: {
      ...jsonFlowChart.main,
      steps: processSteps(jsonFlowChart.main.steps)
    }
  }
}
